import { useState, useEffect, useRef} from 'react'
import { IOrg, IOrgInput, IOrgResponse } from "../../../../types/org";
import { FormFeedback, Input, Label } from 'reactstrap';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { IValueListLight } from '../../../../types/valuelist';
import { GET_ORG_DETAILS, UPDATE_ORG, IS_SCOPE, GET_ORG_RELATIONSHIPS} from '../../../../graphql/org-gql';
import AuthWrapper from '../../../common/auth-wrapper';
import { SaveIcon } from '../../../common/icons';
import useOrgTypes from '../../../../hook/useOrgTypes';
import { ORG_VALUE_LISTS } from '../../../../graphql/valuelist-gql';
import { UserRoleType } from '../../../../types/constants';
import { notifyError, notifySuccess } from '../../../../services/notification';
import Select from '../../../common/select/select';
import Textarea, { TextareaHandler } from '../../../common/textarea/textarea';
import { validOrgName, validOrgNameErrorMessage } from '../../../../utils/commons';
import useAuth from '../../../../hook/useAuth';
import { useFrench } from '../../../french/french-context';
import Button from '../../../common/button';
import SpinnerOverlay from '../../../common/spinner/spinner';

type OrgInfoProps = {
    org: IOrg;
    onOrgSelect?: (org: IOrg) => void;
}

const OrgInformation = (props:OrgInfoProps) => {
    const { org,onOrgSelect} = props
    const {isAdmin, isCoordinator,isFrenchTranslator} = useAuth()
    const parent = org?.parentOrg===null?org.details.category:org

    const { orgTypes } = useOrgTypes(parent)

    const [getOrgDetail, {loading:getOrgLoading, data: orgData }] = useLazyQuery<IOrgResponse>(GET_ORG_DETAILS)
    const [ updateOrg,{ loading: updateOrgLoading } ] = useMutation(UPDATE_ORG);
    const [ isOrgScope,{loading:isOrgScopeLoading,data:orgScope} ] = useLazyQuery(IS_SCOPE);

    const [editedOrg, setEditedOrg] = useState<Partial<IOrgInput>>({})

    const client = useApolloClient()
    const { orgValueLists } =  client.readQuery({query: ORG_VALUE_LISTS})

    const descRef = useRef<TextareaHandler>(null)
    const [validated, setValidated] = useState<boolean>(false)

    const [orgNameError,setOrgNameError] = useState<string>('')
    const [acronymError,setAcronymError] = useState<string>('')
    const [cloned,setCloned] = useState<boolean>(false);

    const [frenchState] = useFrench();

    useEffect(() => {
        setCloned(!!org.details?.shareLinkId)
        if (org) {
            let _editedOrg: IOrgInput = {
                orgName: org.orgName,
                orgAcronym: org.details?.orgAcronym,
                description: org.details?.description,
                orgTypeId: org.details?.orgType?.id,
                orgPrefixId: org.details?.orgPrefix?.id,
                frenchName: org.details?.frenchName,
                frenchNameTranslated: org.details?.frenchNameTranslated,
                frenchDescription: org.details?.frenchDescription,
                frenchDescriptionTranslated: org.details?.frenchDescriptionTranslated

            }
            setEditedOrg(_editedOrg)
        }

        org?.id &&
            isOrgScope({
                variables: {
                orgId: org.id,
                isLowerLevel: false
                }
            })
    }, [org])

    useEffect(()=>{
        if (orgData?.org && onOrgSelect) {
            onOrgSelect(orgData.org)
        }
    },[orgData])

    useEffect(() => {
        if(!isAdmin && !isCoordinator){
            setValidated(true)
        }else{
            let isValid: boolean = !!descRef.current?.validate() && !orgNameError && !acronymError
            setValidated(isValid)
        }

    }, [editedOrg])

    const validate = (): boolean => {
        if(isFrenchTranslator) return true;
        if (!editedOrg?.orgName?.trim()) {
            setOrgNameError('Name is required')
            return false
        }
        return !orgNameError && !acronymError && !!descRef.current?.validate()
    }

    const changeOrgName = (event: any) => {
        let value = event.target.value
        if (value && value.length>200) {
            setOrgNameError('This value is too long. It should have 200 characters or fewer.')
        } else if (value && !validOrgName(value)) {
            setOrgNameError(validOrgNameErrorMessage)
        } else if (value.trim()) {
            setOrgNameError('')
        }
        setEditedOrg({ ...editedOrg, orgName: value,frenchNameTranslated: false})
    }

    const changeOrgType = (event: any) => {
        setEditedOrg({ ...editedOrg, orgTypeId: event.target.value })
    }

    const changeOrgDesc = (value: string) => {
        if (value && value.trim().length > 0) {
            setEditedOrg({ ...editedOrg, description: value, frenchDescriptionTranslated: false});
        }
        else {
            setEditedOrg({ ...editedOrg, description: '', frenchDescription: '', frenchDescriptionTranslated: false });
        }
    }

    const changeOrgAcronym = (event: any) => {
        let value = event.target.value
        if (value && value.length > 30) {
            setAcronymError('This value is too long. It should have 30 characters or fewer.')
        } else {
            setAcronymError('')
        }
        setEditedOrg({...editedOrg, orgAcronym: value})
    }

    const changeOrgPrefix = (event: any) => {
        setEditedOrg({...editedOrg, orgPrefixId: event.target.value})
    }

    const changeNameTranslated=(event: any) => {
        setEditedOrg({...editedOrg, frenchNameTranslated: event.target.checked})
    }

    const changeDescTranslated=(event: any) => {
        setEditedOrg({...editedOrg, frenchDescriptionTranslated: event.target.checked})
    }

    const onUpdateHandle = () => {
        if (!validate()) return

        updateOrg({
            variables: {
                id: org?.id,
                input: {
                    ...editedOrg
                }
            },
            update(cache) {
                //console.log('Cache before eviction:', cache.extract());
                cache.evict({ id: 'ROOT_QUERY', fieldName: 'orgPath' });
                cache.gc();
                //console.log('Cache after eviction:', cache.extract());    
            },
            refetchQueries:getRefetchQueries(),
            awaitRefetchQueries: true,
            onError: (error) => {
                if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                    error.graphQLErrors.forEach((graphQLError) => {
                        // if this happens, there might be a validation mismatch between the client and server
                        if (graphQLError.extensions && graphQLError.extensions.field === "orgName") {
                            setOrgNameError(error.message);
                        }
                    });
                }
            },
            onCompleted: (data) => {
                getOrgDetail({ variables: { id: org?.id } })
                data && notifySuccess()
            }
        })
    }

    const getRefetchQueries = () => {
        let refetchQueries:any = [];
        if(org?.parentOrg?.id){
            refetchQueries = [...refetchQueries,{query:GET_ORG_RELATIONSHIPS,variables: {orgId:org.parentOrg.id}}]
        }
        return refetchQueries;
    }

    const orgTypeName = () =>{
        if(editedOrg.orgTypeId && orgTypes){
            const orgType = orgTypes.find((item:IValueListLight)=>item.id===editedOrg.orgTypeId)
            return orgType?orgType.label:'';
        }
    }

    return (
        <div className="org-detail">
            {(updateOrgLoading||getOrgLoading||isOrgScopeLoading) && <SpinnerOverlay />}
            <div className='row'>
                <div className='col-md-3'><Label for="name" role="region" aria-label='Name (required)'>Name (required)</Label></div>
                <div  className='col-md-9'>
                    {(isAdmin||(!isAdmin && isCoordinator && org?.parentOrg))
                        && <Input id="name" type="text" style={{ width: '100%' }} value={editedOrg.orgName || ''} onChange={changeOrgName} invalid={!!orgNameError} disabled={cloned}/>
                    }
                    {((!isAdmin && !isCoordinator && isFrenchTranslator)||(!isAdmin && isCoordinator && !org?.parentOrg)) && <div>{editedOrg.orgName || ''}</div>}
                    <FormFeedback>{orgNameError}</FormFeedback>
                </div>
            </div>
           {
            frenchState.isFrenchChecked && <><div className='row'>
                <div className='col-md-3'><Label for="name" role="region" aria-label='Name (French)'>Name (French)</Label></div>
                <div  className='col-md-9'>
                    <div lang="fr">{editedOrg.frenchName || ''}</div>
                </div>
            </div>
            <div className='row'><div className='col-md-3'>
                <Input id="nameTranslated" type="checkbox" checked={editedOrg?.frenchNameTranslated || false} onChange={changeNameTranslated} disabled={!editedOrg.frenchName} /> {' '}
                <Label for="nameTranslated" role="region" aria-label='Translated name'>Translated Name</Label>
                </div>
            </div>
            </>
           }
            <div className='row'>
                <div className='col-md-3'><Label for="type" role="region" aria-label='Type (required)'>Type (required)</Label></div>
                <div className='col-md-9' >
                {!isFrenchTranslator &&<Select id="type" value={editedOrg?.orgTypeId || ''} onChange={changeOrgType} required style={{width:'300px'}} disabled={cloned}>
                        {orgTypes?.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
                    </Select>}
                {isFrenchTranslator && <div>{orgTypeName()}</div>}
                </div>
            </div>
            <div className='row'>
                <div  className='col-md-3'><Label for="acronym" role="region" aria-label='Acronym'>Acronym</Label></div>
                {!isFrenchTranslator &&<AuthWrapper role={[UserRoleType.ADMINISTRATOR, UserRoleType.COORDINATOR]}>
                    {(authorized:boolean)=>
                        <div className='col-md-9'>
                            <Input id="acronym" type="text" style={{ width: '100%' }} value={editedOrg.orgAcronym || ''} onChange={changeOrgAcronym} invalid={!!acronymError} disabled={!(authorized&&orgScope?.isScope)||cloned}/>
                            <FormFeedback>{acronymError}</FormFeedback>
                        </div>
                    }
                </AuthWrapper>}
                {isFrenchTranslator && <div className='col-md-9'>{editedOrg.orgAcronym || ''}</div>}
            </div>
            <div className='row'>
                <div  className='col-md-3'><Label for="Description" role="region" aria-label='Description'>Description</Label></div>
                <div className='col-md-9' role="region" aria-label='Description'>
                {!isFrenchTranslator &&<Textarea
                        id="Description"
                        ref={descRef}
                        maxLength={1500}
                        rows={4}
                        value={editedOrg.description || ''}
                        onValueChange={changeOrgDesc}
                        disabled={cloned}
                    />}
                {isFrenchTranslator && <div>{editedOrg.description || ''}</div>}
                </div>
            </div>
            {
            frenchState.isFrenchChecked && <><div className='row'>
                <div className='col-md-3'><Label for="name" role="region" aria-label='French Description'>Description (French)</Label></div>
                <div  className='col-md-9'>
                    <div lang="fr">{editedOrg.frenchDescription || ''}</div>
                </div>
            </div>
            <div className='row'><div className='col-md-3'>
                <Input id="descTranslated" type="checkbox" checked={editedOrg?.frenchDescriptionTranslated || false} onChange={changeDescTranslated}   disabled={cloned || !editedOrg.frenchDescription}/> {' '}
                <Label for="descTranslated" role="region" aria-label='Translated name'>Translated Description</Label>
                </div>
            </div>
            </>
           }
            <div className='row'>
                <div className='col-md-3'><Label for="Prefix" role="region" aria-label='Prefix'>Prefix</Label></div>
                <div className='col-md-9'>
                    {isAdmin && <AuthWrapper role={[UserRoleType.ADMINISTRATOR]}>
                        {(authorized: boolean) =>
                            <Select id="Prefix" value={editedOrg?.orgPrefixId || ''} onChange={changeOrgPrefix} disabled={!authorized||cloned} style={{ width: '300px' }}>
                                <option aria-label='empty' value=''></option>
                                {orgValueLists.orgPrefixes.map((item: IValueListLight) => <option key={item.id} value={item.id}>{item.label}</option>)}
                            </Select>
                        }
                    </AuthWrapper>}
                    {!isAdmin  &&
                            <div>{org.details?.orgPrefix?.label}</div>
                    }
                </div>
            </div>

            <div className='row'>
                <div className='col-md-3'>Category</div>
                <div id="Category" className='col-md-9' role="region" aria-label='Category' >{org?.details?.category?.categoryName}</div>
            </div>
            {(isAdmin || isCoordinator || frenchState.isFrenchChecked) &&
                <Button color="primary" onClick={onUpdateHandle} disabled={!validated||cloned}>
                    <SaveIcon/>
                    &nbsp;<span role="region" aria-label='save'>Save</span>
                </Button>
            }
        </div>
    )
}

export default OrgInformation
